// File: noviopus-ats-client/src/features/OnBoardingFlow/sagas.ts
import { put, takeLatest, call, select, take } from 'redux-saga/effects';
import { 
  setOnboardingStatus, 
  checkOnboardingStatus, 
  onboardingCheckFailed,
  advanceOnboardingStage 
} from './onBoardingFlowSlice';
import { fetchOnboardingStatusApi, advanceOnboardingStageApi } from './api';
import { getTrackNameForAccount, getStepsForTrack } from '@/Account/onboardingTracks'; 
import { selectOnboardingState } from './selectors';
import { setCurrentAccount } from '@/features/Auth/authSlice'; 
import { selectCurrentAccount } from '@/features/Auth/selectors'; 
import { push } from '@/App/router/actions';
import { trackNoviopusEvent } from '@/utils/analytics';

const SET_CREATED_COMPANY = 'app/Company/SET_CREATED_COMPANY';

// Saga: Check Status
function* checkStageSaga() {
  try {
    const status = yield call(fetchOnboardingStatusApi);
    
    if (status) {
      const trackName = getTrackNameForAccount(status);
      const trackSteps = getStepsForTrack(trackName);
      
      let currentStepPath: string | null = null;
      
      if (status.onboardingCompleted) {
        currentStepPath = '/dashboard/recruitment'; 
      } else {
        currentStepPath = trackSteps[status.onboardingStage] || trackSteps[0];
      }

      const payload = {
        stage: status.onboardingStage,
        isCompleted: status.onboardingCompleted,
        trackName,
        trackSteps,
        redirectPath: currentStepPath,
        completionMetrics: status.completionMetrics 
      };

      yield put(setOnboardingStatus(payload));
    } else {
      yield put(onboardingCheckFailed());
    }
  } catch (error) {
    console.error("[OnboardingSaga] Check failed", error);
    yield put(onboardingCheckFailed());
  }
}

// Saga: Advance Stage
function* advanceStageSaga(action: any) {
  try {
    const stage = action.payload; 
    const result = yield call(advanceOnboardingStageApi, stage);
    
    if (result) {
        // Sync Auth State
        const currentAccount = yield select(selectCurrentAccount);
        if (currentAccount) {
            yield put(setCurrentAccount({
                ...currentAccount,
                onboardingStage: result.onboardingStage,
                onboardingComplete: result.onboardingCompleted,
                onboardingTrack: result.onboardingTrack || currentAccount.onboardingTrack
            }));
        }

        const trackName = getTrackNameForAccount(result);
        const trackSteps = getStepsForTrack(trackName);
        
        let nextPath;
        if (result.onboardingCompleted) {
            nextPath = '/dashboard/recruitment';

            // ==========================================
            // TELEMETRY: Business Account Created
            // ==========================================
            // Extract the account type (e.g., 'company', 'HR', etc.) 
            // Fallback to 'company' if missing
            const accType = currentAccount?.type || 'company';
            
            trackNoviopusEvent('business_account_created', {
              account_type: accType.toLowerCase()
            });
            // ==========================================
            
        } else {
            nextPath = trackSteps[result.onboardingStage];
        }

        const payload = {
            stage: result.onboardingStage,
            isCompleted: result.onboardingCompleted,
            trackName,
            trackSteps,
            redirectPath: nextPath,
            completionMetrics: result.completionMetrics
        };
        
        yield put(setOnboardingStatus(payload));
        
        // FIX: Explicitly navigate if we are complete
        if (result.onboardingCompleted) {
            yield put(push('/dashboard/recruitment'));
        }
    }
  } catch (error) {
    console.error("[OnboardingSaga] Advance failed", error);
    yield put(onboardingCheckFailed());
  }
}

function* watchCompanyCreationSuccess() {
    while (true) {
        yield take(SET_CREATED_COMPANY);
        const state = yield select(selectOnboardingState);
        if (!state.isCompleted && state.stage === 1) { 
             yield put(advanceOnboardingStage(1));
        }
    }
}

export function* onBoardingFlowSaga() {
  yield takeLatest(checkOnboardingStatus.type, checkStageSaga);
  yield takeLatest(advanceOnboardingStage.type, advanceStageSaga);
  yield call(watchCompanyCreationSuccess); 
}